import { ExternalLink, Info } from 'lucide-react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';

const SOURCES = [
  { id: 'salaire', label: 'Salaire' },
  { id: 'alternance', label: 'Alternance' },
  { id: 'stage', label: 'Stage' },
  { id: 'job_etudiant', label: 'Job étudiant' },
  { id: 'chomage', label: 'Chômage' },
  { id: 'micro_entreprise', label: 'Micro-entreprise' },
  { id: 'bourse', label: 'Bourse' },
  { id: 'aucun', label: 'Aucun revenu' },
];

const SITUATIONS = [
  { id: 'celibataire', label: 'Célibataire' },
  { id: 'pacse', label: 'Pacsé·e' },
  { id: 'marie', label: 'Marié·e' },
];

const pill = (selected, isDark) => `px-3 py-1.5 rounded-lg text-xs border transition ${
  selected ? 'bg-[#81d8d0] text-black border-[#81d8d0]' : isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-black/10 hover:bg-black/5'
}`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs opacity-70 mb-1 block">{label}</span>
    {children}
  </label>
);

// Étape « première déclaration » : identité, adresse au 1er janvier, situation,
// rattachement passé, sources de revenus + checklist. Aucun mot de passe demandé.
export const FirstDeclarationForm = ({ form, onChange, checklist, years, isDark }) => {
  const set = (key, value) => onChange({ ...form, [key]: value });
  const inputClass = isDark ? 'bg-white/5 border-white/10' : 'bg-white';
  const toggleSource = (id) => set('sources_revenus', form.sources_revenus.includes(id)
    ? form.sources_revenus.filter((s) => s !== id)
    : [...form.sources_revenus, id]);

  return (
    <div data-testid="first-declaration-form">
      <Card className={`${isDark ? '' : 'text-[#1d1d1f] '}p-5 mb-6 border ${isDark ? 'bg-[#81d8d0]/8 border-[#81d8d0]/25' : 'bg-[#81d8d0]/10 border-[#81d8d0]/30'}`}>
        <div className="flex gap-3 text-sm">
          <Info className="w-5 h-5 text-[#81d8d0] flex-shrink-0 mt-0.5" />
          <p className="opacity-80">
            Bonne nouvelle : la première déclaration se fait <strong>en ligne sur impots.gouv.fr</strong>, avec le service
            « Vous déclarez pour la première fois ». Pas besoin d'avis d'imposition. Tu déclares tes revenus {years.anneeRevenus} en {years.anneeDeclaration}.
          </p>
        </div>
      </Card>

      <Card className={`${isDark ? '' : 'text-[#1d1d1f] '}p-6 mb-6 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`}>
        <h3 className="font-medium mb-4">Tes infos (gardées dans ton dossier {years.anneeRevenus})</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nom">
            <Input value={form.nom} onChange={(e) => set('nom', e.target.value)} className={inputClass} data-testid="fd-nom" maxLength={80} />
          </Field>
          <Field label="Prénom">
            <Input value={form.prenom} onChange={(e) => set('prenom', e.target.value)} className={inputClass} data-testid="fd-prenom" maxLength={80} />
          </Field>
          <Field label="Date de naissance">
            <Input type="date" value={form.date_naissance} onChange={(e) => set('date_naissance', e.target.value)} className={inputClass} data-testid="fd-naissance" />
          </Field>
          <Field label={`Adresse au 1er janvier ${years.anneeDeclaration}`}>
            <Input value={form.adresse_1er_janvier} onChange={(e) => set('adresse_1er_janvier', e.target.value)} className={inputClass} data-testid="fd-adresse" maxLength={240} />
          </Field>
        </div>

        <div className="mt-5">
          <span className="text-xs opacity-70 mb-2 block">Situation familiale au 31 décembre {years.anneeRevenus}</span>
          <div className="flex flex-wrap gap-2">
            {SITUATIONS.map((s) => (
              <button key={s.id} type="button" className={pill(form.situation_familiale === s.id, isDark)} onClick={() => set('situation_familiale', s.id)} data-testid={`fd-situation-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <span className="text-xs opacity-70 mb-2 block">Tu as déjà été rattaché·e au foyer fiscal de tes parents ?</span>
          <div className="flex flex-wrap gap-2 items-center">
            {[{ v: true, l: 'Oui' }, { v: false, l: 'Non' }].map((o) => (
              <button key={String(o.v)} type="button" className={pill(form.deja_rattache === o.v, isDark)} onClick={() => set('deja_rattache', o.v)} data-testid={`fd-rattache-${o.v ? 'yes' : 'no'}`}>
                {o.l}
              </button>
            ))}
            {form.deja_rattache && (
              <Input
                type="number"
                placeholder={`Dernière année (ex. ${years.anneeRevenusN1})`}
                value={form.annee_rattachement}
                onChange={(e) => set('annee_rattachement', e.target.value)}
                className={`max-w-[220px] ${inputClass}`}
                data-testid="fd-annee-rattachement"
              />
            )}
          </div>
        </div>

        <div className="mt-5">
          <span className="text-xs opacity-70 mb-2 block">Tes sources de revenus en {years.anneeRevenus}</span>
          <div className="flex flex-wrap gap-2">
            {SOURCES.map((s) => (
              <button key={s.id} type="button" className={pill(form.sources_revenus.includes(s.id), isDark)} onClick={() => toggleSource(s.id)} data-testid={`fd-source-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <Field label="Employeur(s), séparés par des virgules">
            <Input value={form.employeurs} onChange={(e) => set('employeurs', e.target.value)} className={inputClass} data-testid="fd-employeurs" />
          </Field>
        </div>
        <p className="text-[11px] opacity-50 mt-4">Skipya ne te demande jamais ton mot de passe impots.gouv.fr.</p>
      </Card>

      {checklist.length > 0 && (
        <Card className={`${isDark ? '' : 'text-[#1d1d1f] '}p-6 mb-6 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`} data-testid="first-declaration-checklist">
          <h3 className="font-medium mb-3">À préparer pour ta première déclaration</h3>
          <ol className="space-y-3">
            {checklist.map((c, i) => (
              <li key={c.id} className="flex gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-[#81d8d0]/15 text-[#81d8d0] flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                <div>
                  <div className="font-medium">{c.title}</div>
                  <div className="text-xs opacity-70">{c.detail}</div>
                </div>
              </li>
            ))}
          </ol>
          <a href="https://www.impots.gouv.fr/accueil" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#81d8d0] mt-4">
            Ouvrir impots.gouv.fr <ExternalLink className="w-3 h-3" />
          </a>
        </Card>
      )}
    </div>
  );
};

export default FirstDeclarationForm;
